import { DndContext, DragMoveEvent, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ColumnPinningState,
  Row,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  DataTableSkeleton,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { DebouncedInput } from 'components/debounced-input';
import { Note } from 'components/note';
import { MIN_WIDTH } from 'components/table/column-actions';
import { ColumnFilter } from 'components/table/column-filter';
import { HiddenColumns } from 'components/table/hidden-columns';
import { TableActions } from 'components/table/table-actions';
import { useAtomValue } from 'jotai';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import { useColumnState } from 'lib/hooks/useColumnState';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { DECIMAL_FORMATTER, FORMATTER } from 'lib/ui';
import plur from 'plur';
import { useCallback, useMemo, useRef, useState, Fragment } from 'react';
import { PackageEvaluationAssetsResponse } from 'types/packages-api/responses';
import { formatNumber } from 'utils/number';
import { capitalizeFirstLetter } from 'utils/strings';
import { LoadPackage } from '../page';
import style from './assets.module.scss';
import { useCreateEvaluationAssetsAtom } from './atoms';

import { DragCell, DragHeader } from './draggable-table-components';
import {
  getInitialColumnOrder,
  getPinningStyles,
  handleDragEnd,
  handleDragMove,
} from './utils';

const columnHelper = createColumnHelper<PackageEvaluationAssetsResponse>();

const EndDepth = ({
  startDepth,
  endDepth,
}: {
  startDepth: number | null;
  endDepth: number | null;
}) => {
  if (startDepth !== null && endDepth === null) return <>&infin;</>;
  if (endDepth === null) return <>--</>;
  return <>{DECIMAL_FORMATTER.format(endDepth)}</>;
};

const columns = [
  columnHelper.accessor((row) => row.asset.name, {
    id: 'assetName',
    header: 'Asset',
    size: 130,
    cell: ({ row }) => {
      const value = row.original.asset;
      return value.salesforceHref ? (
        <a
          style={{ cursor: 'pointer' }}
          rel="noreferrer"
          href={value.salesforceHref}
          target="_blank"
        >
          {value.name || 'Empty Name'}
        </a>
      ) : (
        value.name || 'Empty Name'
      );
    },
    footer: () => {
      return 'Totals';
    },
    meta: {
      filterVariant: 'text',
    },
  }),
  columnHelper.accessor((row) => row.fullLegalDescription, {
    id: 'fullLegalDescription',
    header: 'Full Legal Description',
    size: 320,
    meta: {
      filterVariant: 'text',
    },
  }),
  columnHelper.accessor((row) => row.startDepth, {
    id: 'startDepth',
    header: 'Start Depth (ft)',
    enableGlobalFilter: false,
    cell: (row) => {
      const v = row.getValue();
      return v !== null && typeof v === 'number'
        ? DECIMAL_FORMATTER.format(v)
        : null;
    },
    meta: {
      filterVariant: 'range',
    },
  }),

  columnHelper.accessor((row) => row.endDepth, {
    id: 'endDepth',
    header: 'End Depth (ft)',
    enableGlobalFilter: false,
    cell: ({ row }) => {
      const startDepth = row.original.startDepth;
      const endDepth = row.original.endDepth;
      return <EndDepth startDepth={startDepth} endDepth={endDepth} />;
    },
    meta: {
      filterVariant: 'range',
    },
    enableColumnFilter: false,
  }),
  columnHelper.accessor((row) => row.depth, {
    id: 'depth',
    header: 'Legacy Depth',
    size: 270,
    cell: (row) => {
      const value = row.getValue();
      return (
        <div className={style.notesColumn}>
          <Note noteText={value} />
        </div>
      );
    },
    meta: {
      filterVariant: 'text',
    },
  }),
  columnHelper.accessor((row) => row.notes, {
    id: 'notes',
    header: 'Notes',
    size: 270,
    cell: (row) => {
      const value = row.getValue();
      return (
        <div className={style.notesColumn}>
          <Note noteText={value} />
        </div>
      );
    },
    meta: {
      filterVariant: 'text',
    },
  }),
  columnHelper.accessor((row) => row.assetType, {
    id: 'assetType',
    header: 'Asset Type',
    cell: (row) => {
      const value = row.getValue();
      return value ? capitalizeFirstLetter(value) : null;
    },
    meta: {
      filterVariant: 'text',
    },
  }),
  columnHelper.accessor((row) => row.netAcreage, {
    id: 'netAcreage',
    header: 'Acres',
    size: 120,
    cell: ({ row }) => {
      const value = row.original.netAcreage;
      const units = row.original.netAcreageUnits;
      return formatNumber({
        value,
        minPrecision: 2,
        maxPrecision: 2,
        includeCommas: true,
        fallback: '--',
        unit: units ?? undefined,
      });
    },
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('targetPricePerAcre', {
    id: 'targetPricePerAcre',
    header: 'Target $/Acre',
    enableGlobalFilter: false,
    cell: (row) => {
      const v = row.getValue();
      return v ? FORMATTER.format(v) : '--';
    },
    footer: ({ table }) => {
      const filteredRows = table.getFilteredRowModel().rows;

      // Initialize accumulators for acreage and target price
      let accumulatedTargetPrice = 0;
      let accumulatedAcreage = 0;

      // Accumulate total targetPrice and acreage from filtered rows
      filteredRows.forEach((row) => {
        accumulatedTargetPrice += row.original.targetPrice;
        accumulatedAcreage += row.original.netAcreage || 0; // Fallback to 0 if netAcreage is undefined
      });

      // Calculate targetPricePerRoyaltyAcre
      const targetPricePerRoyaltyAcre =
        accumulatedAcreage === 0
          ? 0
          : accumulatedTargetPrice / accumulatedAcreage;

      // Format and return the calculated value
      return FORMATTER.format(targetPricePerRoyaltyAcre);
    },
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('targetPrice', {
    id: 'targetPrice',
    header: 'Target Price',
    enableGlobalFilter: false,
    cell: (row) => {
      const value = row.getValue();
      return FORMATTER.format(value);
    },
    footer: ({ table }) => {
      return FORMATTER.format(
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + row.original.targetPrice, 0)
      );
    },
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('maxPricePerAcre', {
    id: 'maxPricePerAcre',
    header: 'Max $/Acre',
    enableGlobalFilter: false,
    cell: (row) => {
      const value = row.getValue();
      return FORMATTER.format(value);
    },
    footer: ({ table }) => {
      const filteredRows = table.getFilteredRowModel().rows;

      // Initialize accumulators for acreage and Max price
      let accumulatedMaxPrice = 0;
      let accumulatedAcreage = 0;

      // Accumulate total maxPrice and acreage from filtered rows
      filteredRows.forEach((row) => {
        accumulatedMaxPrice += row.original.maxPrice;
        accumulatedAcreage += row.original.netAcreage || 0; // Fallback to 0 if netAcreage is undefined
      });

      // Calculate targetMaxPricePerRoyaltyAcre
      const targetMaxPricePerRoyaltyAcre =
        accumulatedAcreage === 0 ? 0 : accumulatedMaxPrice / accumulatedAcreage;

      // Format and return the calculated value
      return FORMATTER.format(targetMaxPricePerRoyaltyAcre);
    },
    meta: {
      filterVariant: 'range',
    },
  }),
  columnHelper.accessor('maxPrice', {
    id: 'maxPrice',
    header: 'Max Price',
    enableGlobalFilter: false,
    cell: (row) => {
      const value = row.getValue();
      return FORMATTER.format(value);
    },
    footer: ({ table }) => {
      return FORMATTER.format(
        table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + row.original.maxPrice, 0)
      );
    },
    meta: {
      filterVariant: 'range',
    },
  }),
];

const Assets = ({ href }: { href: string }) => {
  const allocationsAtom = useCreateEvaluationAssetsAtom(href);
  const allocations = useAtomValue(allocationsAtom);

  if (allocations.data && 'error' in allocations.data)
    return (
      <InlineNotification
        kind="error"
        title={allocations.data.error}
        lowContrast
      />
    );

  const allocationsData = allocations.data?.map((el, i) => {
    return {
      id: String(i),
      ...el,
    };
  });

  if (!allocationsData) return <InlineLoading />;

  return <AssetsTable data={allocationsData} />;
};

const AssetsTable = ({ data }: { data: PackageEvaluationAssetsResponse[] }) => {
  const assetsTableId = 'package-assets-table';
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnVisibility, setColumnVisibility] =
    useColumnVisibility(assetsTableId);

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(assetsTableId);

  const [columnSizing, setColumnSizing] = useColumnSizing(assetsTableId);

  const initialColumnPinning: ColumnPinningState = {
    left: ['assetName', 'fullLegalDescription'],
  };

  const initialColumnOrder = useMemo(() => getInitialColumnOrder(columns), []);

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId: assetsTableId,
      initialColumnOrder,
      initialColumnPinning,
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);
  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const table = useReactTable({
    data: data || [],
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: MIN_WIDTH,
    },
    columns: columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      columnFilters,
      columnSizing,
      columnOrder,
      columnPinning,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    initialState: {
      columnPinning: {
        left: ['assetName', 'fullLegalDescription'],
      },
      columnOrder: initialColumnOrder,
    },
    meta: {
      reset: {
        resetCustomizations: () => {
          setIsColumnFilterActive(false);
        },
      },
    },
  });

  const { rows } = table.getRowModel();

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 20,
  });

  if (!data) {
    return (
      <DataTableSkeleton
        showHeader={false}
        columnCount={columns.length}
        rowCount={10}
        showToolbar={false}
      />
    );
  }

  const isColumunVisible = table.getVisibleLeafColumns().length > 0;

  return (
    <LoadPackage
      render={() => (
        <>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={(e) =>
              handleDragEnd(
                e,
                columnPinning,
                columnOrder,
                setColumnOrder,
                setColumnPinning,
                hoveredColumnIdRef
              )
            }
            modifiers={[restrictToHorizontalAxis]}
            onDragMove={onDragMove}
          >
            <div ref={parentRef} className={style.stackTableArea}>
              <div className={style.tableContext}>
                <div className={style.stackSearchArea}>
                  <DebouncedInput
                    className={style.stackSearchField}
                    value={globalFilter ?? ''}
                    onChange={(value) => setGlobalFilter(String(value).trim())}
                    placeholder="Search columns..."
                  />
                </div>
                <TableActions
                  table={table}
                  isColumnFilterActive={isColumnFilterActive}
                  setIsColumnFilterActive={(isColumnFilterActive) =>
                    setIsColumnFilterActive(isColumnFilterActive)
                  }
                ></TableActions>
              </div>

              {isColumunVisible ? (
                <table className={style.stackTable}>
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Fragment key={headerGroup.id}>
                        <tr>
                          <SortableContext
                            items={columnOrder}
                            strategy={horizontalListSortingStrategy}
                          >
                            {headerGroup.headers.map((header) => (
                              <DragHeader
                                key={header.id}
                                header={header}
                                hoveredColumnIdRef={hoveredColumnIdRef}
                              />
                            ))}
                          </SortableContext>
                        </tr>

                        {isColumnFilterActive ? (
                          <tr>
                            <ColumnFilter
                              pinStyleGenerator={getPinningStyles}
                              headers={headerGroup.headers}
                            />
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </thead>
                  <tbody
                    style={{ minHeight: `${virtualizer.getTotalSize()}px` }}
                  >
                    {virtualizer.getVirtualItems().map((virtualRow, index) => {
                      const row = rows[
                        virtualRow.index
                      ] as Row<PackageEvaluationAssetsResponse>;
                      return (
                        <tr
                          key={row.id}
                          style={{
                            minHeight: `${virtualRow.size}px`,
                            transform: `translateY(${
                              virtualRow.start - index * virtualRow.size
                            }px)`,
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <SortableContext
                              key={cell.id}
                              items={columnOrder}
                              strategy={horizontalListSortingStrategy}
                            >
                              <DragCell
                                key={cell.id}
                                cell={cell}
                                row={row}
                                hoveredColumnIdRef={hoveredColumnIdRef}
                              />
                            </SortableContext>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {table.getFooterGroups().map((footerGroup) => (
                      <tr key={footerGroup.id}>
                        {footerGroup.headers.map((header) => (
                          <th
                            style={{ ...getPinningStyles(header.column) }}
                            key={header.id}
                            colSpan={header.colSpan}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.footer,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </tfoot>
                </table>
              ) : (
                <HiddenColumns className={style.hiddenColumn}></HiddenColumns>
              )}
            </div>

            {isColumunVisible ? (
              <div className={style.totalRowCount}>
                <label className={style.totalRows}>
                  {DECIMAL_FORMATTER.format(
                    table.getFilteredRowModel().rows.length
                  )}
                </label>
                {plur('row', table.getFilteredRowModel().rows.length)}
              </div>
            ) : null}
          </DndContext>
        </>
      )}
    />
  );
};

export { Assets };
